import { useQuery } from "@tanstack/react-query";
import { Search, Truck } from "lucide-react";
import * as React from "react";

import { providers } from "@/api/endpoints";
import type { VerificationStatus } from "@/api/types";
import { useDebounced } from "@/hooks/useDebounced";
import { FilterBar, PageHeader, Pagination } from "@/components/PageHeader";
import { Badge, toneFor } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { EmptyState, ErrorState, TableSkeleton } from "@/components/ui/feedback";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatDate, humanize } from "@/lib/utils";

const VERIFICATION_OPTIONS = [
  { label: "Verified", value: "VERIFIED" },
  { label: "Pending", value: "PENDING" },
  { label: "Pending review", value: "PENDING_REVIEW" },
  { label: "Rejected", value: "REJECTED" },
];

export default function Providers() {
  const [page, setPage] = React.useState(1);
  const [search, setSearch] = React.useState("");
  const [city, setCity] = React.useState("");
  const [verification, setVerification] = React.useState("");

  const q = useDebounced(search);
  const debouncedCity = useDebounced(city);

  React.useEffect(() => setPage(1), [q, debouncedCity, verification]);

  const query = useQuery({
    queryKey: ["providers", { page, q, debouncedCity, verification }],
    queryFn: () =>
      providers.list({
        page,
        page_size: 20,
        q: q || undefined,
        city: debouncedCity || undefined,
        verification_status: (verification || undefined) as
          | VerificationStatus
          | undefined,
      }),
  });

  return (
    <>
      <PageHeader
        title="Service providers"
        description="Everyone offering equipment or services on the marketplace."
      />

      <FilterBar>
        <div className="relative min-w-[15rem] flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by business, phone or email"
            className="pl-8"
            aria-label="Search providers"
          />
        </div>
        <div className="w-40">
          <Input
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="City"
            aria-label="Filter by city"
          />
        </div>
        <div className="w-44">
          <Select
            value={verification}
            onValueChange={setVerification}
            options={VERIFICATION_OPTIONS}
            placeholder="Any verification"
            aria-label="Filter by verification status"
          />
        </div>
      </FilterBar>

      <Card className="overflow-hidden">
        {query.isLoading ? (
          <TableSkeleton cols={6} />
        ) : query.isError ? (
          <ErrorState error={query.error} onRetry={() => query.refetch()} />
        ) : query.data && query.data.items.length > 0 ? (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Business</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Verification</TableHead>
                  <TableHead className="text-right">Rating</TableHead>
                  <TableHead>Online</TableHead>
                  <TableHead>Joined</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {query.data.items.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell className="font-medium">{p.business_name}</TableCell>
                    <TableCell className="tabular text-muted-foreground">
                      {p.phone ?? p.email ?? "—"}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {[p.city, p.state].filter(Boolean).join(", ") || "—"}
                    </TableCell>
                    <TableCell>
                      <Badge variant={toneFor(p.verification_status)}>
                        {humanize(p.verification_status)}
                      </Badge>
                    </TableCell>
                    <TableCell className="tabular text-right">
                      {p.rating_count > 0
                        ? `${p.rating_average.toFixed(1)} (${p.rating_count})`
                        : "—"}
                    </TableCell>
                    <TableCell>
                      <span
                        className="inline-flex items-center gap-1.5 text-xs"
                        title={p.is_online ? "Online" : "Offline"}
                      >
                        <span
                          aria-hidden
                          className="size-2 rounded-full"
                          style={{
                            background: p.is_online
                              ? "var(--status-good)"
                              : "hsl(var(--muted-foreground))",
                          }}
                        />
                        {p.is_online ? "Online" : "Offline"}
                      </span>
                    </TableCell>
                    <TableCell className="tabular text-muted-foreground">
                      {formatDate(p.created_at)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Pagination
              page={query.data.page}
              pageSize={query.data.pageSize}
              total={query.data.total}
              onPageChange={setPage}
            />
          </>
        ) : (
          <EmptyState
            icon={Truck}
            title="No providers match those filters"
            description="Try clearing the city or verification filter."
          />
        )}
      </Card>
    </>
  );
}
